import React, { useState, useEffect, createContext, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme, PaletteMode } from '@mui/material';
import Layout from './components/Layout';
import HomeEntry from './pages/HomeEntry';
import VehicleList from './pages/vehicles/VehicleList';
import VehicleForm from './pages/vehicles/VehicleForm';
import VehicleDetails from './pages/vehicles/VehicleDetails';
import VehicleExpenses from './pages/vehicles/VehicleExpenses';
import ExpenseList from './pages/expenses/ExpenseList';
import ExpenseForm from './pages/expenses/ExpenseForm';
import ExpenseDetails from './pages/expenses/ExpenseDetails';
import RecurringCashMovementList from './pages/movimentacoes/RecurringCashMovementList';
import RecurringCashMovementForm from './pages/movimentacoes/RecurringCashMovementForm';
import SaleList from './pages/sales/SaleList';
import SaleForm from './pages/sales/SaleForm';
import SaleDetails from './pages/sales/SaleDetails';
import RentalList from './pages/rentals/RentalList';
import RentalForm from './pages/rentals/RentalForm';
import RentalDetails from './pages/rentals/RentalDetails';
import Reports from './pages/Reports';
import Caixa from './pages/Caixa';
import MovimentacoesList from './pages/movimentacoes/MovimentacoesList';
import ConciliacaoBancaria from './pages/conciliacao/ConciliacaoBancaria';
import Contracts from './pages/contracts/Contracts';
import ConsignacaoView from './pages/contracts/ConsignacaoView';
import VendaView from './pages/contracts/VendaView';
import Login from './pages/auth/Login';
import AccessDenied from './components/utils.js/AccessDenied';
import AuthGuard from './components/AuthGuard';
import UserList from './pages/users/UserList';
import UserForm from './pages/users/UserForm';
import UserDetails from './pages/users/UserDetails';
import BancoList from './pages/admin/BancoList';
import ActivityLogsPage from './pages/admin/ActivityLogsPage';
import ProtectedRoute from './components/utils.js/ProtectedRoute';
import EmployeeList from './pages/employees/EmployeeList';
import VitrinePage from './pages/vitrine/VitrinePage';
import VitrineDetailPage from './pages/vitrine/VitrineDetailPage';
import LeadsList from './pages/leads/LeadsList';
import DrePage from './pages/dre/DrePage';
import ProposalPage from './pages/proposals/ProposalPage';
import VitrineAnalyticsPage from './pages/vitrineAnalytics/VitrineAnalyticsPage';
import OlxIntegrationPage from './pages/integrations/OlxIntegrationPage';


// Contexto para o tema
export const ColorModeContext = createContext({
  toggleColorMode: () => {},
  mode: 'dark' as PaletteMode,
});

function App() {
  // Recupera o modo do localStorage ou usa light como padrão
  const [mode, setMode] = useState<PaletteMode>(
    localStorage.getItem('themeMode') as PaletteMode || 'light'
  );

  // Salva a preferência no localStorage quando o modo mudar
  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  // Contexto para alternar o modo de cor
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
      mode,
    }),
    [mode]
  );

  // Cria o tema com base no modo atual (vermelho no lugar do azul)
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: '#F00000',
            light: '#FA0000',
            dark: '#D20000',
            contrastText: '#ffffff',
          },
          secondary: {
            main: '#e63946',
            light: '#f87171',
            dark: '#b91c1c',
            contrastText: '#ffffff',
          },
          error: {
            main: '#d32f2f',
          },
          warning: {
            main: '#ff9800',
          },
          info: {
            main: '#29b6f6',
          },
          success: {
            main: '#4caf50',
          },
          background: {
            default: mode === 'dark' ? '#121212' : '#f5f5f5',
            paper: mode === 'dark' ? '#1e1e1e' : '#ffffff',
          },
          text: {
            primary: mode === 'dark' ? '#e0e0e0' : '#212121',
            secondary: mode === 'dark' ? '#aaaaaa' : '#757575',
          },
        },
        typography: {
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          h4: {
            fontWeight: 500,
          },
          button: {
            textTransform: 'none', // Botões sem texto todo em maiúsculo
            fontWeight: 500,
          },
        },
        shape: {
          borderRadius: 8, // Bordas mais arredondadas
        },
        components: {
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
                backgroundColor: mode === 'dark' ? '#1e1e1e' : '#ffffff',
                color: mode === 'dark' ? '#e0e0e0' : '#212121',
              },
              elevation1: {
                boxShadow: mode === 'dark' 
                  ? '0px 2px 10px rgba(0, 0, 0, 0.15)' 
                  : '0px 2px 10px rgba(0, 0, 0, 0.08)',
              },
            },
            defaultProps: {
              elevation: 1,
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: mode === 'dark'
                    ? '0px 2px 10px rgba(0, 0, 0, 0.2)'
                    : '0px 2px 10px rgba(0, 0, 0, 0.1)',
                },
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                background: mode === 'dark' ? '#1e1e1e' : '#fafafa',
                boxShadow: mode === 'dark'
                  ? '0px 1px 3px rgba(0, 0, 0, 0.2)'
                  : '0px 1px 3px rgba(0, 0, 0, 0.08)',
                color: mode === 'dark' ? '#ffffff' : '#333333',
              },
            },
          },
          MuiToolbar: {
            styleOverrides: {
              root: {
                color: mode === 'dark' ? '#ffffff' : '#333333',
              }
            }
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root': {
                  backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.23)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#F00000',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                },
                '& .MuiInputBase-input': {
                  color: mode === 'dark' ? '#e0e0e0' : '#212121',
                },
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.23)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#F00000',
                },
              },
              notchedOutline: {
                borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
              },
            },
          },
          MuiInputBase: {
            styleOverrides: {
              root: {
                color: mode === 'dark' ? '#e0e0e0' : '#212121',
              },
              input: {
                '&::placeholder': {
                  color: mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',
                  opacity: 1,
                },
              },
            },
          },
          MuiSelect: {
            styleOverrides: {
              select: {
                backgroundColor: 'transparent',
              },
              icon: {
                color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.54)',
              },
            },
          },
          MuiFormControl: {
            styleOverrides: {
              root: {
                '& .MuiInputLabel-root': {
                  color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                },
              },
            },
          },
          MuiMenu: {
            styleOverrides: {
              paper: {
                backgroundColor: mode === 'dark' ? '#2c2c2c' : '#ffffff',
              },
              list: {
                padding: '8px 0',
              },
            },
          },
          MuiMenuItem: {
            styleOverrides: {
              root: {
                color: mode === 'dark' ? '#e0e0e0' : '#212121',
                '&:hover': {
                  backgroundColor: mode === 'dark' 
                    ? 'rgba(63, 136, 197, 0.15)' 
                    : 'rgba(63, 136, 197, 0.08)',
                },
                '&.Mui-selected': {
                  backgroundColor: mode === 'dark' 
                    ? 'rgba(63, 136, 197, 0.2)' 
                    : 'rgba(63, 136, 197, 0.12)',
                  '&:hover': {
                    backgroundColor: mode === 'dark' 
                      ? 'rgba(63, 136, 197, 0.3)' 
                      : 'rgba(63, 136, 197, 0.2)',
                  },
                },
              },
            },
          },
          MuiAutocomplete: {
            styleOverrides: {
              paper: {
                backgroundColor: mode === 'dark' ? '#2c2c2c' : '#ffffff',
              },
              listbox: {
                padding: '8px 0',
                '& .MuiAutocomplete-option': {
                  '&[aria-selected="true"]': {
                    backgroundColor: mode === 'dark' 
                      ? 'rgba(63, 136, 197, 0.2)' 
                      : 'rgba(63, 136, 197, 0.12)',
                  },
                  '&[data-focus="true"]': {
                    backgroundColor: mode === 'dark' 
                      ? 'rgba(63, 136, 197, 0.15)' 
                      : 'rgba(63, 136, 197, 0.08)',
                  },
                },
              },
              endAdornment: {
                color: mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.54)',
              },
            },
          },
          MuiTableContainer: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'dark' ? '#1e1e1e' : '#ffffff',
              },
            },
          },
          MuiTableHead: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(63, 136, 197, 0.08)',
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                borderBottomColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
              },
              head: {
                fontWeight: 600,
                color: mode === 'dark' ? '#e0e0e0' : '#333333',
              },
            },
          },
          MuiTableRow: {
            styleOverrides: {
              root: {
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.04)'
                    : 'rgba(0, 0, 0, 0.04)',
                },
                '&.Mui-selected': {
                  backgroundColor: mode === 'dark'
                    ? 'rgba(63, 136, 197, 0.16)'
                    : 'rgba(63, 136, 197, 0.08)',
                },
              },
            },
          },
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#3f88c5',
                  borderRadius: '4px',
                },
              },
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                color: 'inherit',
              }
            }
          },
          MuiDivider: {
            styleOverrides: {
              root: {
                borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
              }
            }
          },
          MuiBackdrop: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'dark' 
                  ? 'rgba(0, 0, 0, 0.7)' 
                  : 'rgba(0, 0, 0, 0.5)',
              }
            }
          },
          MuiPopover: {
            styleOverrides: {
              paper: {
                backgroundColor: mode === 'dark' ? '#2c2c2c' : '#ffffff',
                boxShadow: mode === 'dark'
                  ? '0px 5px 15px rgba(0, 0, 0, 0.3)'
                  : '0px 5px 15px rgba(0, 0, 0, 0.1)',
              }
            }
          },
        },
      }),
    [mode]
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Routes>
            {/* Rotas públicas - sem a barra lateral */}
            <Route path="/login" element={<Login />} />
            <Route path="/acesso-negado" element={<AccessDenied />} />
            <Route path="/vitrine" element={<VitrinePage />} />
            <Route path="/vitrine/veiculo/:id" element={<VitrineDetailPage />} />
            
            {/* Rotas protegidas - com Layout e AuthGuard */}
            <Route path="/" element={<Layout />}>
              <Route element={<AuthGuard />}>
                <Route index element={<HomeEntry />} />
                <Route path="veiculos" element={<VehicleList />} />
                <Route element={<AuthGuard denyRoles={['viewer']} />}>
                  <Route path="veiculos/novo" element={<VehicleForm />} />
                  <Route path="veiculos/editar/:id" element={<VehicleForm />} />
                </Route>
                <Route path="veiculos/:id" element={<VehicleDetails />} />
                <Route path="vendas" element={<SaleList />} />
                <Route element={<AuthGuard denyRoles={['viewer']} />}>
                  <Route path="vendas/novo" element={<SaleForm />} />
                  <Route path="vendas/editar/:id" element={<SaleForm />} />
                </Route>
                <Route path="vendas/:id" element={<SaleDetails />} />
                <Route path="locacoes" element={<RentalList />} />
                <Route element={<AuthGuard denyRoles={['viewer']} />}>
                  <Route path="locacoes/novo" element={<RentalForm />} />
                  <Route path="locacoes/editar/:id" element={<RentalForm />} />
                </Route>
                <Route path="locacoes/:id" element={<RentalDetails />} />
                <Route path="contratos" element={<Contracts />} />
                <Route path="contratos/consignacao/:vehicleId" element={<ConsignacaoView />} />
                <Route path="contratos/venda/:saleId" element={<VendaView />} />
                <Route path="propostas" element={<ProposalPage />} />
                <Route path="leads" element={<LeadsList />} />
                <Route path="leads/carros-antigos" element={<Navigate to="/leads" replace />} />

                <Route element={<AuthGuard allowedRoles={['admin']} />}>
                  <Route path="veiculos/:id/despesas" element={<VehicleExpenses />} />
                  <Route path="despesas" element={<ExpenseList />} />
                  <Route path="despesas/novo" element={<ExpenseForm />} />
                  <Route path="despesas/editar/:id" element={<ExpenseForm />} />
                  <Route path="despesas/:id" element={<ExpenseDetails />} />
                  <Route path="relatorios" element={<Reports />} />
                  <Route path="dre" element={<DrePage />} />
                  <Route path="caixa" element={<Caixa />} />
                  <Route path="conciliacao" element={<ConciliacaoBancaria />} />
                  <Route path="movimentacoes" element={<MovimentacoesList />} />
                  <Route path="movimentacoes/recorrentes" element={<RecurringCashMovementList />} />
                  <Route path="movimentacoes/recorrentes/novo" element={<RecurringCashMovementForm />} />
                  <Route path="movimentacoes/recorrentes/editar/:id" element={<RecurringCashMovementForm />} />
                  <Route path="funcionarios" element={<EmployeeList />} />
                  <Route path="registro-atividades" element={<ActivityLogsPage />} />
                  <Route path="acessos-vitrine" element={<VitrineAnalyticsPage />} />
                  <Route path="integracoes/olx" element={<OlxIntegrationPage />} />
                  <Route path="usuarios" element={<UserList />} />
                  <Route path="usuarios/novo" element={<UserForm />} />
                  <Route path="usuarios/:id" element={<UserDetails />} />
                  <Route path="usuarios/editar/:id" element={<UserForm />} />
                  <Route path="/bancos" element={<ProtectedRoute element={<BancoList />} />} />
                </Route>
              </Route>
            </Route>
            
            {/* Redireciona para o login se a rota não existir */}
            <Route path="*" element={<Navigate to="/vitrine" replace />} />
          </Routes>
        </Router>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
